import { API_BASE_URL } from '@/config/environment';

/**
 * Centralized utility for handling all image URLs
 * This ensures consistent URL construction across the app
 */
export const getImageUrl = (url?: string, type: 'profile' | 'library' | 'kit' | 'vehicle' | 'signature' | 'document' = 'profile'): string | undefined => {
  if (!url) return undefined;
  
  // If it's already a full URL, return as is
  if (url.startsWith('http')) {
    // Normalize absolute URLs: force HTTPS domain from API_BASE_URL
    try {
      const absolute = new URL(url);
      const base = new URL(API_BASE_URL.replace('/api/v1', ''));
      // Keep path/query, replace origin with our HTTPS origin
      return `${base.origin}${absolute.pathname}${absolute.search}`;
    } catch {
      // Fallback to original if parsing fails
      return url;
    }
  }
  
  // If it's a relative path, construct the full URL
  const baseUrl = API_BASE_URL.replace('/api/v1', '');
  
  // Handle different path formats
  let imagePath = url;
  if (!imagePath.startsWith('/')) {
    imagePath = '/' + imagePath;
  }
  
  // Convert old profile_pictures path to new profile-pictures format
  if (imagePath.includes('/profile_pictures/')) {
    imagePath = imagePath.replace('/profile_pictures/', '/profile-pictures/');
  }
  
  // Ensure the path starts with the correct upload directory
  if (!imagePath.includes('/uploads/') && !imagePath.includes('/profile-pictures/')) {
    switch (type) {
      case 'profile':
        if (!imagePath.includes('/profile-pictures/')) {
          imagePath = `/profile-pictures${imagePath}`;
        }
        break;
      case 'library':
        if (!imagePath.includes('/uploads/library/')) {
          imagePath = `/uploads/library${imagePath}`;
        }
        break;
      case 'kit':
        if (!imagePath.includes('/uploads/kit_images/')) {
          imagePath = `/uploads/kit_images${imagePath}`;
        }
        break;
      case 'vehicle':
        if (!imagePath.includes('/uploads/vehicles/')) {
          imagePath = `/uploads/vehicles${imagePath}`;
        }
        break;
      case 'signature':
        if (!imagePath.includes('/uploads/signatures/')) {
          imagePath = `/uploads/signatures${imagePath}`;
        }
        break;
      case 'document':
        if (!imagePath.includes('/uploads/documents/')) {
          imagePath = `/uploads/documents${imagePath}`;
        }
        break;
    }
  }
  
  const fullUrl = `${baseUrl}${imagePath}`;
  
  return fullUrl;
};

/**
 * Legacy function for profile pictures (for backward compatibility)
 */
export const getProfilePictureUrl = (url?: string): string | undefined => {
  return getImageUrl(url, 'profile');
};

/**
 * For uploads stored as `/uploads/...` or inline `data:` / absolute URLs (signatures, POP links, job photos).
 * Matches the pattern used in FieldOperationsScreen for installation forms.
 */
export const getUploadOrDataUrl = (filePath?: string | null): string | null => {
  if (!filePath) return null;
  if (
    filePath.startsWith('http://') ||
    filePath.startsWith('https://') ||
    filePath.startsWith('data:')
  ) {
    return filePath;
  }

  const baseUrl = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
  return `${baseUrl}${filePath.startsWith('/') ? filePath : `/${filePath}`}`;
};
